export type ArenaLighting = {
  readonly accent: string;
  readonly accentSecond: string;
  readonly background: string;
  readonly panel: string;
  readonly panelRaised: string;
  readonly text: string;
  readonly muted: string;
  readonly line: string;
  readonly glow: string;
  readonly primaryText: string;
};

type Rgb = { readonly r: number; readonly g: number; readonly b: number };

const fallbackAccent = '#7c3aed';
const clampByte = (value: number): number => Math.max(0, Math.min(255, Math.round(value)));
const byte = (value: number): string => clampByte(value).toString(16).padStart(2, '0');
const toHex = ({ r, g, b }: Rgb): string => `#${byte(r)}${byte(g)}${byte(b)}`;

const parseHex = (value: string): Rgb | null => {
  const normalized = value.trim().replace(/^#/, '');
  const expanded =
    normalized.length === 3
      ? normalized
          .split('')
          .map((part) => `${part}${part}`)
          .join('')
      : normalized;
  if (!/^[\da-f]{6}$/i.test(expanded)) return null;
  return {
    r: Number.parseInt(expanded.slice(0, 2), 16),
    g: Number.parseInt(expanded.slice(2, 4), 16),
    b: Number.parseInt(expanded.slice(4, 6), 16),
  };
};

const mix = (from: Rgb, to: Rgb, amount: number): Rgb => ({
  r: from.r + (to.r - from.r) * amount,
  g: from.g + (to.g - from.g) * amount,
  b: from.b + (to.b - from.b) * amount,
});

const channel = (value: number): number => {
  const normalized = value / 255;
  return normalized <= 0.04045 ? normalized / 12.92 : ((normalized + 0.055) / 1.055) ** 2.4;
};

const luminance = (color: Rgb): number =>
  0.2126 * channel(color.r) + 0.7152 * channel(color.g) + 0.0722 * channel(color.b);

export const contrastRatio = (foreground: string, background: string): number => {
  const first = parseHex(foreground) ?? parseHex(fallbackAccent)!;
  const second = parseHex(background) ?? parseHex('#030813')!;
  const bright = Math.max(luminance(first), luminance(second));
  const dark = Math.min(luminance(first), luminance(second));
  return (bright + 0.05) / (dark + 0.05);
};

export const arenaLighting = (input: string | null | undefined): ArenaLighting => {
  const raw = parseHex(input ?? '') ?? parseHex(fallbackAccent)!;
  const white = parseHex('#ffffff')!;
  const ink = parseHex('#020611')!;
  const readableAccent = luminance(raw) < 0.13 ? mix(raw, white, 0.36) : raw;
  const accent = toHex(readableAccent);
  const accentSecond = toHex(mix(readableAccent, white, 0.28));
  const background = toHex(mix(ink, readableAccent, 0.075));
  const panel = toHex(mix(ink, readableAccent, 0.14));
  const panelRaisedColor = mix(ink, readableAccent, 0.21);
  const panelRaised = toHex(panelRaisedColor);
  const text = '#f8fbff';
  const mutedCandidate = toHex(mix(parseHex('#8fa2c2')!, readableAccent, 0.12));
  const muted = contrastRatio(mutedCandidate, background) >= 4.5 ? mutedCandidate : '#aebbd2';
  const primaryText = contrastRatio('#05070d', accent) >= 4.5 ? '#05070d' : '#ffffff';
  return {
    accent,
    accentSecond,
    background,
    panel,
    panelRaised,
    text,
    muted,
    line: toHex(mix(panelRaisedColor, white, 0.22)),
    glow: `${accent}52`,
    primaryText,
  };
};

export const arenaThemeProperties = (
  lighting: ArenaLighting,
): Readonly<Record<string, string>> => ({
  '--arena-accent': lighting.accent,
  '--arena-accent-2': lighting.accentSecond,
  '--arena-bg': lighting.background,
  '--arena-panel': lighting.panel,
  '--arena-panel-2': lighting.panelRaised,
  '--arena-text': lighting.text,
  '--arena-muted': lighting.muted,
  '--arena-line': lighting.line,
  '--arena-glow': lighting.glow,
  '--arena-primary-text': lighting.primaryText,
  '--bg': lighting.background,
  '--card': lighting.panel,
  '--card-2': lighting.panelRaised,
  '--violet': lighting.accent,
  '--blue': lighting.accentSecond,
  '--text': lighting.text,
  '--muted': lighting.muted,
  '--line': lighting.line,
});
